import React, { useEffect, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import { brand } from '../mock';

/**
 * Sticky WhatsApp button — always visible bottom-right.
 * - Pulses softly to attract attention without being annoying.
 * - On hover/desktop, shows an inline tooltip with brand-aligned styling.
 * - Skips itself in the admin area.
 */
const WhatsAppFloat = () => {
  const { t, lang } = useLang();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only mount on public pages, not /admin
    const onAdmin = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
    if (onAdmin) return;
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const phone = brand.phones[0].tel.replace(/\D/g, '');
  const greeting =
    lang === 'fr'
      ? 'Bonjour, je souhaite une consultation gratuite chez Gold Care Esthetic.'
      : lang === 'ar'
      ? 'مرحباً، أرغب في استشارة مجانية في جولد كير إستيتيك.'
      : 'Hello, I would like a free consultation with Gold Care Esthetic.';
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(greeting)}`;

  return (
    <div
      data-testid="whatsapp-float"
      className="fixed z-[9999] bottom-24 md:bottom-6 ltr:right-5 rtl:left-5"
      style={{ direction: 'ltr' }}
    >
      {/* Tooltip card */}
      {open && (
        <div className="absolute bottom-[78px] right-0 w-[260px] bg-white text-[#0B3D2E] rounded-2xl shadow-[0_18px_50px_-15px_rgba(0,0,0,0.4)] p-4 animate-[fadeInUp_0.3s_ease-out_forwards]">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
            className="absolute top-2 right-2 text-[#0B3D2E]/50 hover:text-[#0B3D2E]"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#A8832E] font-semibold">
            {t('wa.online', 'Online now')}
          </div>
          <div className="font-serif-display text-lg leading-tight mt-1">
            {t('wa.headline', 'Talk to a medical advisor')}
          </div>
          <div className="text-xs text-[#0B3D2E]/65 mt-1.5 leading-relaxed">
            {t('wa.sub', 'Get a personalized plan in 24 hours — free, confidential.')}
          </div>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-cta-link"
            className="mt-3 inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1da851] text-white font-semibold text-sm transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            {t('wa.start', 'Start chat')}
          </a>
        </div>
      )}

      {/* Floating button */}
      <button
        type="button"
        aria-label={t('wa.tooltip', 'Chat with us on WhatsApp')}
        data-testid="whatsapp-float-btn"
        onClick={() => setOpen((v) => !v)}
        className="group relative w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] hover:bg-[#1da851] text-white flex items-center justify-center shadow-[0_10px_30px_-5px_rgba(37,211,102,0.6)] transition-transform hover:scale-105"
      >
        {/* pulsing ring */}
        <span className="absolute inset-0 rounded-full bg-[#25D366]/40 animate-ping" />
        <span className="absolute inset-0 rounded-full ring-2 ring-[#D4AF37]/0 group-hover:ring-[#D4AF37]/80 transition-all" />
        <MessageCircle className="relative w-6 h-6 md:w-7 md:h-7" strokeWidth={2} />
      </button>
    </div>
  );
};

export default WhatsAppFloat;
